import React, { useState } from 'react';
import { Download, FileText, ChevronDown } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface ReportDoc {
  id: string; type: 'progress' | 'attendance' | 'transcript' | 'notice';
  titleMs: string; titleEn: string;
  date: string; term: string;
  past?: boolean;
}

const DOCS: ReportDoc[] = [
  { id:'p1', type:'progress',    titleMs:'Laporan Kemajuan — Penggal 1 2025/2026', titleEn:'Progress Report — Term 1 2025/2026',     date:'15 Mac 2026',   term:'Penggal 1 2025/2026' },
  { id:'p2', type:'progress',    titleMs:'Laporan Kemajuan — Penggal 2 2024/2025', titleEn:'Progress Report — Term 2 2024/2025',     date:'10 Ogos 2025',  term:'Penggal 2 2024/2025', past:true },
  { id:'p3', type:'progress',    titleMs:'Laporan Kemajuan — Penggal 1 2024/2025', titleEn:'Progress Report — Term 1 2024/2025',     date:'28 Mac 2025',   term:'Penggal 1 2024/2025', past:true },
  { id:'a1', type:'attendance',  titleMs:'Laporan Kehadiran — Jun 2025',           titleEn:'Attendance Summary — June 2025',         date:'30 Jun 2025',   term:'Jun 2025' },
  { id:'a2', type:'attendance',  titleMs:'Laporan Kehadiran — Penggal 1 2025',     titleEn:'Attendance Summary — Term 1 2025',       date:'28 Mac 2025',   term:'Penggal 1 2025', past:true },
  { id:'t1', type:'transcript',  titleMs:'Transkrip Akademik 2024/2025',           titleEn:'Academic Transcript 2024/2025',          date:'15 Jan 2026',   term:'2024/2025', past:true },
  { id:'n1', type:'notice',      titleMs:'Notis: Peperiksaan Pertengahan Penggal', titleEn:'Notice: Mid-term Examination',           date:'10 Mei 2025',   term:'Penggal 1 2025' },
  { id:'n2', type:'notice',      titleMs:'Notis: Aktiviti Ko-Kurikulum',           titleEn:'Notice: Co-curricular Activity',         date:'3 Jun 2025',    term:'Penggal 1 2025' },
];

const TAB_TYPES: { key: ReportDoc['type'] | 'all'; ms: string; en: string }[] = [
  { key:'all',        ms:'Semua',             en:'All'               },
  { key:'progress',   ms:'Laporan Kemajuan',  en:'Progress Reports'  },
  { key:'attendance', ms:'Kehadiran',         en:'Attendance'        },
  { key:'transcript', ms:'Transkrip',         en:'Transcripts'       },
  { key:'notice',     ms:'Notis',             en:'Notices'           },
];

const TYPE_ICON_COLOR: Record<ReportDoc['type'], { bg: string; color: string }> = {
  progress:   { bg: '#DBEAFE', color: '#1D4ED8' },
  attendance: { bg: '#DCFCE7', color: '#0D7A4E' },
  transcript: { bg: '#F5F3FF', color: '#7C3AED' },
  notice:     { bg: '#FFFBEB', color: '#D97706' },
};

export function ParentLaporanPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Reports' : 'Laporan');

  const [activeType, setActiveType] = useState<ReportDoc['type'] | 'all'>('all');
  const [showPast, setShowPast] = useState(false);

  const filtered = DOCS.filter(d =>
    (activeType === 'all' || d.type === activeType) &&
    (showPast || !d.past)
  );

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Reports' : 'Laporan'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {E ? 'Downloadable reports and documents for Ahmad Zulfikri' : 'Laporan dan dokumen yang boleh dimuat turun untuk Ahmad Zulfikri'}
          </p>
        </div>
      </div>

      {/* Tab filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {TAB_TYPES.map(t => (
          <button
            key={t.key}
            className={`pill-filter${activeType === t.key ? ' active' : ''}`}
            onClick={() => setActiveType(t.key)}
          >
            {E ? t.en : t.ms}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: T.color.textSecondary, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showPast}
              onChange={e => setShowPast(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {E ? 'Show past years' : 'Semua tahun'}
          </label>
        </div>
      </div>

      {/* Document list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: T.color.textSecondary, fontSize: '13px' }}>
            {E ? 'No documents found for this category.' : 'Tiada dokumen dijumpai untuk kategori ini.'}
          </div>
        )}
        {filtered.map(doc => {
          const ic = TYPE_ICON_COLOR[doc.type];
          return (
            <div key={doc.id} style={{
              background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px',
              padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '16px',
              transition: 'box-shadow 150ms ease',
            }}>
              <div style={{ width: '42px', height: '42px', background: ic.bg, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={20} color={ic.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: T.color.textPrimary, marginBottom: '4px' }}>
                  {E ? doc.titleEn : doc.titleMs}
                </div>
                <div style={{ fontSize: '12px', color: T.color.textSecondary, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <span>{E ? 'Issued:' : 'Dikeluarkan:'} {doc.date}</span>
                  <span style={{ color: '#D1D5DB' }}>·</span>
                  <span>{doc.term}</span>
                  {doc.past && (
                    <span className="badge" style={{ background: '#F3F4F6', color: '#6B7280', fontSize: '10px', padding: '2px 8px' }}>
                      {E ? 'Past year' : 'Tahun lepas'}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="btn-primary btn-sm"
                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}
                title={`SERPS_${doc.titleMs.replace(/\s+/g,'_')}.pdf`}
              >
                <Download size={13} />
                {E ? 'Download PDF' : 'Muat Turun PDF'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
